import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from '../components/DatePicker';
import { reportsAPI } from '../services/api';
import { formatDate, formatDateTime, getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { formatCoordinates } from '../utils/geolocation';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/ReportPages.module.css';

const downloadCsv = (filename, rows) => {
  const escapeCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const AttendanceReportPage = () => {
  const { t } = useLocalization();
  const today = getTodayKualaLumpurISODate();
  const [mode, setMode] = useState('daily');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    if (nextMode === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
    } else if (nextMode === 'monthly') {
      setStartDate(toKualaLumpurISODate(new Date(year, month, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, month + 1, 0)));
    } else if (nextMode === 'yearly') {
      setStartDate(toKualaLumpurISODate(new Date(year, 0, 1)));
      setEndDate(toKualaLumpurISODate(new Date(year, 11, 31)));
    }
    // 'range' keeps current dates
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reportsAPI.attendanceReport({ mode, startDate, endDate });
      setRows(response.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t('attendance_report_load_failed', 'Failed to load attendance report'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // Initial load only; report refresh is user-triggered via View button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalHours = useMemo(
    () => rows.reduce((sum, row) => sum + Number(row.total_hours || 0), 0),
    [rows]
  );

  const handleExport = () => {
    const csvRows = [
      [
        t('reports_date', 'Date'),
        t('reports_user', 'User'),
        t('attendance_first_clock_in', 'First Clock In'),
        t('attendance_last_clock_out', 'Last Clock Out'),
        t('attendance_clock_in_machine_id', 'Clock In Machine ID'),
        t('attendance_clock_in_location', 'Clock In Location'),
        t('attendance_clock_in_gps', 'Clock In GPS'),
        t('attendance_clock_out_machine_id', 'Clock Out Machine ID'),
        t('attendance_clock_out_location', 'Clock Out Location'),
        t('attendance_clock_out_gps', 'Clock Out GPS'),
        t('attendance_total_hours_with_lunch', 'Total Hours (Rounded Down, -1h Lunch)')
      ],
      ...rows.map((row) => [
        row.work_date,
        row.username,
        formatDateTime(row.first_clock_in),
        row.last_clock_out ? formatDateTime(row.last_clock_out) : '-',
        row.first_clock_in_device_id || '-',
        row.first_clock_in_location_name || '-',
        formatCoordinates(row.first_clock_in_latitude, row.first_clock_in_longitude) || '-',
        row.last_clock_out_device_id || '-',
        row.last_clock_out_location_name || '-',
        formatCoordinates(row.last_clock_out_latitude, row.last_clock_out_longitude) || '-',
        row.total_hours,
      ]),
    ];
    downloadCsv(t('attendance_export_filename', 'attendance-report.csv'), csvRows);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('attendance_report_title', 'Attendance Report')}</h1>
        <p>{t('attendance_report_subtitle', 'Based on first clock in and last clock out, minus 1 hour lunch.')}</p>
      </div>

      <div className={styles.controls}>
        <div className={styles.modeButtons}>
          {['daily', 'monthly', 'yearly', 'range'].map((item) => (
            <button
              key={item}
              type="button"
              className={`${styles.modeBtn} ${mode === item ? styles.modeBtnActive : ''}`}
              onClick={() => handleModeChange(item)}
            >
              {item === 'daily'
                ? t('common_daily', 'Daily')
                : item === 'monthly'
                  ? t('common_monthly', 'Monthly')
                  : item === 'yearly'
                    ? t('common_yearly', 'Yearly')
                    : t('common_range', 'Range')}
            </button>
          ))}
        </div>
        <DatePicker value={startDate} onChange={mode === 'range' ? setStartDate : undefined} placeholder={t('tasks_start_date', 'Start date')} className={styles.dateInput} disabled={mode !== 'range'} />
        <DatePicker value={endDate} onChange={mode === 'range' ? setEndDate : undefined} placeholder={t('tasks_end_date', 'End date')} className={styles.dateInput} disabled={mode !== 'range'} />
        <button className={styles.actionBtn} type="button" onClick={fetchReport}>{t('reports_view', 'View')}</button>
        <button className={styles.actionBtnSecondary} type="button" onClick={handleExport} disabled={rows.length === 0}>{t('reports_export_excel', 'Export Excel')}</button>
      </div>

      <div className={styles.summaryBar}>{t('attendance_total_hours', 'Total Hours')}: <strong>{totalHours}</strong></div>

      {loading ? (
        <p>{t('attendance_loading', 'Loading attendance report...')}</p>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('reports_date', 'Date')}</th>
                <th>{t('reports_user', 'User')}</th>
                <th>{t('attendance_first_clock_in', 'First Clock In')}</th>
                <th>{t('attendance_last_clock_out', 'Last Clock Out')}</th>
                <th>{t('attendance_clock_in_machine_id', 'Clock In Machine ID')}</th>
                <th>{t('attendance_clock_in_location', 'Clock In Location')}</th>
                <th>{t('attendance_clock_in_gps', 'Clock In GPS')}</th>
                <th>{t('attendance_clock_out_machine_id', 'Clock Out Machine ID')}</th>
                <th>{t('attendance_clock_out_location', 'Clock Out Location')}</th>
                <th>{t('attendance_clock_out_gps', 'Clock Out GPS')}</th>
                <th>{t('attendance_total_hours', 'Total Hours')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className={styles.empty}>{t('attendance_no_data', 'No attendance data found.')}</td>
                </tr>
              ) : rows.map((row, idx) => (
                <tr key={`${row.user_id}-${row.work_date}-${idx}`}>
                  <td>{formatDate(row.work_date)}</td>
                  <td>{row.username}</td>
                  <td>{formatDateTime(row.first_clock_in)}</td>
                  <td>{row.last_clock_out ? formatDateTime(row.last_clock_out) : '-'}</td>
                  <td>{row.first_clock_in_device_id || '-'}</td>
                  <td>{row.first_clock_in_location_name || '-'}</td>
                  <td>{formatCoordinates(row.first_clock_in_latitude, row.first_clock_in_longitude) || '-'}</td>
                  <td>{row.last_clock_out_device_id || '-'}</td>
                  <td>{row.last_clock_out_location_name || '-'}</td>
                  <td>{formatCoordinates(row.last_clock_out_latitude, row.last_clock_out_longitude) || '-'}</td>
                  <td>{row.total_hours}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AttendanceReportPage;
